import {
    ApiShopperLandingPageLink,
    BrandChatMessage,
    BrandChatReply,
} from '@storefronts/common-types'
import { useCallback, useContext, useRef, useState } from 'react'
import { sendBrandChatTurn } from '../../../api'
import { LandingPageLinksContext } from '../../context/LandingPageLinksContext'

export type BrandChatEntry =
    | { role: 'shopper'; text: string }
    | {
          role: 'brand'
          reply: BrandChatReply
          /** Links for the reply's cards and comparison, keyed by Shopify product id. */
          links: Map<string, ApiShopperLandingPageLink>
      }

const toMessages = (entries: BrandChatEntry[]): BrandChatMessage[] =>
    entries.map((entry) =>
        entry.role === 'shopper'
            ? { role: 'shopper', text: entry.text }
            : { role: 'brand', text: entry.reply.text },
    )

export const useBrandChat = (landingPageHash: string) => {
    const { fetchAndCacheLinkForProduct } = useContext(LandingPageLinksContext)
    const [entries, setEntries] = useState<BrandChatEntry[]>([])
    const [pending, setPending] = useState(false)
    const [progress, setProgress] = useState<string | null>(null)
    const [failed, setFailed] = useState(false)
    const started = useRef(false)

    const requestReply = useCallback(
        async (history: BrandChatEntry[]) => {
            setPending(true)
            setFailed(false)
            setProgress(null)
            try {
                const reply = await sendBrandChatTurn(
                    landingPageHash,
                    toMessages(history),
                    setProgress,
                )
                const productIds = [
                    ...new Set([
                        ...(reply.card ? [reply.card.productId] : []),
                        ...reply.productIds,
                        ...(reply.comparison?.productIds ?? []),
                    ]),
                ]
                const links = await Promise.all(
                    productIds.map((id) => fetchAndCacheLinkForProduct(id)),
                )
                setEntries([
                    ...history,
                    {
                        role: 'brand',
                        reply,
                        links: new Map(
                            links.flatMap((link) =>
                                link ? [[link.shopifyId, link] as const] : [],
                            ),
                        ),
                    },
                ])
            } catch {
                setFailed(true)
            } finally {
                setPending(false)
                setProgress(null)
            }
        },
        [landingPageHash, fetchAndCacheLinkForProduct],
    )

    const start = useCallback(() => {
        if (started.current) return
        started.current = true
        void requestReply([])
    }, [requestReply])

    const send = useCallback(
        (text: string) => {
            const trimmed = text.trim()
            if (!trimmed || pending) return
            // A question asked from the page starts the chat without the opener.
            started.current = true
            const history: BrandChatEntry[] = [
                ...entries,
                { role: 'shopper', text: trimmed },
            ]
            setEntries(history)
            void requestReply(history)
        },
        [entries, pending, requestReply],
    )

    const retry = useCallback(() => {
        void requestReply(entries)
    }, [entries, requestReply])

    return { entries, pending, progress, failed, start, send, retry }
}
